import {
  type ChartingLibraryWidgetOptions,
  type IChartingLibraryWidget,
  type LanguageCode,
  type ResolutionString,
  widget,
} from "@ekidenfi/tradingview-chart";
import styled from "@emotion/styled";
import { Flex, Spinner } from "@radix-ui/themes";
import type React from "react";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import { useStores } from "@stores";
import type { ChartTpSlPreview } from "@stores/TradeboxStore";

import { useMedia } from "@hooks/useMedia";
import { observer } from "mobx-react-lite";
import { EkidenDatafeed } from "./EkidenDatafeed";
import { type ChartTpSlOverlayIds, applyTpSlOverlays, clearTpSlOverlays } from "./chartTpSlOverlay";

export interface ChartContainerProps {
  symbol: ChartingLibraryWidgetOptions["symbol"];
  interval: ChartingLibraryWidgetOptions["interval"];
  datafeedUrl: string;
  libraryPath: ChartingLibraryWidgetOptions["library_path"];
  fullscreen: ChartingLibraryWidgetOptions["fullscreen"];
  autosize: ChartingLibraryWidgetOptions["autosize"];
  studiesOverrides: ChartingLibraryWidgetOptions["studies_overrides"];
  container: ChartingLibraryWidgetOptions["container"];
}

const getLanguageFromURL = (): LanguageCode | null => {
  const regex = /[\?&]lang=([^&#]*)/;
  const results = regex.exec(location.search);
  return results === null ? null : (decodeURIComponent(results[1].replace(/\+/g, " ")) as LanguageCode);
};

export const TradingViewPro: React.FC = observer(() => {
  const { marketStore, tradeboxStore } = useStores();
  const symbol = marketStore.activeMarket?.symbol;
  const chartTpSlPreview = tradeboxStore.chartTpSlPreview;
  return <TradingViewProMemo symbol={symbol} chartTpSlPreview={chartTpSlPreview} />;
});

const TradingViewProMemo: React.FC<{
  symbol?: string;
  chartTpSlPreview: ChartTpSlPreview | null;
}> = memo(({ symbol, chartTpSlPreview }) => {
  const { marketStore } = useStores();
  const [isLoading, setIsLoading] = useState(true);
  const { mobile } = useMedia();

  const chartContainerRef = useRef<HTMLDivElement | null>(null);
  const tvWidgetRef = useRef<IChartingLibraryWidget | null>(null);
  const overlayIdsRef = useRef<ChartTpSlOverlayIds>({ riskReward: null, extras: [] });
  const chartTpSlPreviewRef = useRef(chartTpSlPreview);
  chartTpSlPreviewRef.current = chartTpSlPreview;

  const defaultProps: Omit<ChartContainerProps, "container"> = useMemo(
    () => ({
      symbol,
      interval: "60" as ResolutionString,
      datafeedUrl: "", // Not used with custom datafeed
      libraryPath: "/charting_library/",
      fullscreen: false,
      autosize: true,
      studiesOverrides: {},
    }),
    [symbol],
  );

  // Create datafeed with available symbols from market store
  const datafeed = useMemo(() => {
    const symbols = marketStore.allMarkets.map((m) => m.symbol);
    return new EkidenDatafeed(symbols.length > 0 ? symbols : ["BTC-USDC", "ETH-USDC", "APT-USDC", "SOL-USDC"]);
  }, [marketStore.allMarkets]);

  useEffect(() => {
    let tvWidget: IChartingLibraryWidget | null = null;
    setIsLoading(true);

    const initTradingView = async () => {
      if (!chartContainerRef.current || !defaultProps.symbol) return;

      const widgetOptions: ChartingLibraryWidgetOptions = {
        symbol: defaultProps.symbol,
        datafeed,
        interval: defaultProps.interval,
        container: chartContainerRef.current,
        library_path: defaultProps.libraryPath as string,
        locale: getLanguageFromURL() || "en",
        disabled_features: [
          "use_localstorage_for_settings",
          "study_templates",
          "header_symbol_search",
          "symbol_search_hot_key",
          "header_compare",
          "symbol_info",
          "legend_inplace_edit",
        ],
        fullscreen: defaultProps.fullscreen,
        autosize: defaultProps.autosize,
        studies_overrides: defaultProps.studiesOverrides,
        custom_css_url: "css/style.css",
        loading_screen: {
          backgroundColor: "#141414",
        },
        theme: "dark",
        overrides: {
          "paneProperties.background": "#141414",
          "paneProperties.backgroundType": "solid",
          "paneProperties.topMargin": 20,
          "paneProperties.bottomMargin": 20,
        },
      };

      tvWidget = new widget(widgetOptions);
      tvWidgetRef.current = tvWidget;

      if (mobile) {
        const iframe = chartContainerRef.current.querySelector("iframe");
        if (iframe) {
          iframe.sandbox = "allow-scripts allow-same-origin allow-popups";
          const src = String(iframe.src);
          const blobData = await fetch(src)
            .then((response) => response.text())
            .catch((err) => console.error("Error fetching blob:", err));
          if (typeof blobData === "string") {
            iframe.srcdoc = blobData;
          }
        }
      }

      tvWidget.onChartReady(() => {
        setIsLoading(false);
        if (tvWidgetRef.current) {
          applyTpSlOverlays(tvWidgetRef.current, chartTpSlPreviewRef.current, overlayIdsRef.current);
        }
      });
    };

    initTradingView()
      .catch(console.error)
      .then(() => {
        if (mobile) {
          setIsLoading(false);
        }
      });

    return () => {
      if (tvWidget) {
        clearTpSlOverlays(tvWidget, overlayIdsRef.current);
        tvWidget.remove();
      }
      tvWidgetRef.current = null;
      overlayIdsRef.current = { riskReward: null, extras: [] };
    };
  }, [defaultProps.symbol, datafeed, mobile]);

  useEffect(() => {
    if (!tvWidgetRef.current || isLoading) return;
    applyTpSlOverlays(tvWidgetRef.current, chartTpSlPreview, overlayIdsRef.current);
  }, [chartTpSlPreview, isLoading, symbol]);

  return (
    <Wrapper>
      {isLoading && (
        <LoaderOverlay align="center" justify="center">
          <Spinner size="3" />
        </LoaderOverlay>
      )}
      <TradingViewContainer ref={chartContainerRef} />
    </Wrapper>
  );
});

const Wrapper = styled.div`
  position: relative;
  min-height: 0px;
  height: 100%;
  width: 100%;
`;

const LoaderOverlay = styled(Flex)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #141414;
  z-index: 10;
  border-radius: 8px;
`;

const TradingViewContainer = styled.div`
  min-height: 0px;
  height: 100%;
  width: 100%;

  iframe {
    border-radius: 8px;
  }
`;
